import {DateTime} from "luxon";
import {container} from "tsyringe";
import {QueryParameter} from "../connectivity/query-parameter.interface";
import {DataTransferObject} from "../domain/dtos/data-transfer-object.interface";
import {Entity} from "../domain/entities/entity";
import {ArrayHelper} from "../helpers/array.helper";
import {StringHelper} from "../helpers/string.helper";
import {TransformerHelper} from "../helpers/transformer.helper";
import {TransformerInterface} from "../transformers/transformerInterface";
import {ValueOf} from "../types/value-of.type";
import {ClassOf} from "../types/class-of.type";
import {EntityManager} from "./entity.manager";
import {EntityObjectArray} from "../domain/entity-object-array";
import {UsedColor} from "../types/used-color.type";
import {plainToInstance} from "class-transformer";
import {EntityState} from "./entity-state.enum";
import {ChangeTracker} from "../change-tracking/change-tracker";
import {bufferTime, filter, Subject} from "rxjs";

type BatchRequest<TEntity extends Entity> = {
    resolve?: (value: TEntity | PromiseLike<TEntity>) => void,
    reject?: (reason?: any) => void,
    uid?: string,
    promise?: Promise<TEntity>
}

export abstract class Repository<T extends Entity> {

    protected _requestSubject = new Subject<BatchRequest<T>>();

    protected _preloadPromise: Promise<void> | null = null;

    private _entityType!: typeof Entity;

    private _batchCache: Map<string, BatchRequest<T>> = new Map();

    constructor() {
        // Batch requests: collect all within 50ms window then process as batch
        this._requestSubject.pipe(
            bufferTime(50),
            filter(value => value.length > 0),
        ).subscribe(async (batchRequests) => {
            // Keep original case-sensitive UIDs - Base64URL encoding is case-sensitive
            const uniqueUids = [...new Set(batchRequests.map(req => req.uid).filter(ArrayHelper.notEmpty) as string[])];

            if (uniqueUids.length < 1) {
                return;
            }

            try {
                const result = await this._processBatch(uniqueUids);

                batchRequests.forEach(req => {
                    const entity = result.find(ent => {
                        return req.uid === ent.uid;
                    });

                    entity
                        ? req.resolve?.(entity)
                        : req.reject?.(`Entity ${this._entityType.name} with uid ${req.uid} not found`);
                });
            } catch (error) {
                batchRequests.forEach(req => req.reject!(error));
            }
        });

    }

    public get isDeletable(): boolean {
        // @ts-ignore
        return "deletedAt" in new this._entityType;
    }

    public get entity(): typeof Entity {
        return this._entityType;
    }

    private _entityManager!: EntityManager;

    protected get entityManager(): EntityManager {
        return this._entityManager || (this._entityManager = container.resolve(EntityManager));
    }

    protected get idField(): keyof T {
        return `uid`;
    }

    public abstract create(...args: any[]): Promise<T | null>;

    /**
     * Find entity by UID.
     * If includeDeleted is true and entity is not in cache, fetches from API
     * with soft-delete filter disabled.
     */
    public async findOneById(uid: string, includeDeleted: boolean = false): Promise<T | null> {
        // 1. Try cache first via existing batch mechanism
        const cached = await this.findById([uid]).then(entities => ArrayHelper.first(entities));
        if (cached) {
            return cached;
        }

        // 2. If not found and includeDeleted, fetch directly from API
        if (includeDeleted) {
            const results = await this.entityManager.query<DataTransferObject>(
                this.entity.name,
                [{ key: 'uid', comparator: 'eq', value: uid } as QueryParameter<DataTransferObject>],
                0,
                1,
                [],
                [],
                { includeDeleted: true }
            );

            if (results && results.length > 0) {
                return this._transformDtoToEntity(results[0]);
            }
        }

        return null;
    }

    public async findOneByIdOrDto(idOrDto: string | DataTransferObject): Promise<T | null> {
        return (typeof idOrDto === "string")
            ? this.findOneById(idOrDto)
            : TransformerHelper.fromDTO<T>(idOrDto, this._entityType);
    }

    public async findById(uids: string[]): Promise<EntityObjectArray<T>> {

        const entities: T[] = await Promise.all([...new Set(uids)].map(uid => {
            if (this.entityManager.isLoaded(uid)) {
                return Promise.resolve(this.entityManager.getLoadedEntity(uid) as T);
            }

            if (!this._batchCache.has(uid)) {
                let batchRequest: BatchRequest<T> = {};
                batchRequest.promise = new Promise<T>((resolve, reject) => {
                    batchRequest.reject = reject;
                    batchRequest.resolve = resolve;
                    batchRequest.uid = uid;
                });
                this._batchCache.set(uid, batchRequest);
                this._requestSubject.next(batchRequest);
            }

            // `promise` is assigned together with the cache entry above, so it is always set.
            return this._batchCache.get(uid)!.promise!;
        }));

        return new EntityObjectArray(entities.length, entities);
    }

    public async findOneBy(fieldName: keyof T, value: ValueOf<T>): Promise<T | null> {
        const dto = await this.entityManager.findOneBy<T>(this.entity.name, fieldName, value);

        if (dto?.uid && this.entityManager.isLoaded(dto.uid)) {
            return Promise.resolve(this.entityManager.getLoadedEntity(dto.uid) as T);
        }

        return this._transformDtoToEntity(dto);
    }

    public async findAllBy(fieldName: keyof T, value: ValueOf<T>): Promise<T[]> {
        throw new Error("Method not implemented.");
    }

    public async findAll(includes: string[] = [], limit = -1, offset = 0, excludeDeleted = false): Promise<EntityObjectArray<T>> {

        if (this._preloadPromise !== null) {
            try {
                await this._preloadPromise;
                const entities = this.entityManager.findAllByEntityType<T>(this._entityType) as Array<T & {
                    deletedAt: DateTime | null
                }>;

                return new EntityObjectArray<T>(0,
                    (excludeDeleted && this.isDeletable)
                        ? entities.filter(entity => entity.deletedAt === null)
                        : entities
                );
            } catch {
                this._preloadPromise = null;
            }
        }

        const queryParams: QueryParameter<any>[] = [];
        if (excludeDeleted && this.isDeletable) {
            queryParams.push(<QueryParameter<any>>{
                comparator: `eq`,
                key: `deletedAt`,
                value: null
            });
        }

        const dataTransferObjects = await this.entityManager.findAllByQueryParameters<DataTransferObject>(this.entity.name, queryParams, includes, limit, offset);

        return Promise.all(
            dataTransferObjects.map(async dataTransferObject => await this._transformDtoToEntity(dataTransferObject))
        ).then(
            entities => new EntityObjectArray(0, [...(entities ?? []).filter(ArrayHelper.notEmpty)])
        );
    }

    /**
     * Starts an async preload of all entities into the EntityManager cache.
     * Sets _preloadPromise so findAll() can await it before reading from cache.
     * Called by PersistencePreloader after login.
     */
    public startPreload(): void {
        this._preloadPromise = this.entityManager
            .findAllByQueryParameters(this.entity.name, [], [], -1, 0)
            .then(dtos => Promise.all(dtos.map(dto => this._transformDtoToEntity(dto))))
            .then(() => {});
    }

    public async findAllColors(): Promise<UsedColor[]> {
        return this.entityManager.findAllColors(this.entity.name);
    }

    public isNew(entity: T): boolean {
        return this.entityManager.isNew(entity.uid);
    }

    public unCachingEntity(entity: T): void {
        this._batchCache.delete(entity.uid);
    }

    public async reloadEntity(entity: T): Promise<T> {
        if (!this.entityManager.isLoaded(entity.uid)) {
            console.error(`Inconsistent state of ${entity.constructor.name}! Entity »${entity.uid.toLowerCase()}« is not registered at entity manager.`);
        }

        this.unCachingEntity(entity);
        this.entityManager.unloadEntity(entity);

        // reloads existing
        await this.findOneById(entity.uid);

        return entity;
    }

    protected async _transformDtoToEntity(dto: DataTransferObject | null): Promise<T | null> {
        if (!dto) {
            return null;
        }

        const entityKebabName = StringHelper.toKebabCase(this.entity.name);
        const dependencyContainerKey = `transformer.dto_${entityKebabName}`

        if (container.isRegistered(dependencyContainerKey)) {
            const transformer: TransformerInterface<T, any> = container.resolve(dependencyContainerKey);

            return transformer.transform(dto);
        } else {
            // Cast: `entity` is typed as the abstract `typeof Entity`, but it always holds the
            // concrete class passed to @persistence (see persistence.decorator.ts).
            const entity = ChangeTracker.create(plainToInstance(this.entity as ClassOf<T>, dto));

            this._entityManager.setLoadedEntity(entity, EntityState.PERSISTED);
            return Promise.resolve(entity);
        }

    }

    protected async _processBatch(uids: string[]): Promise<EntityObjectArray<T>> {

        const unregisteredUids = uids.filter(uid => !this.entityManager.isLoaded(uid));
        const registeredUids = Array.from((new Set(uids)).difference(new Set(unregisteredUids)));

        const unregisteredEntities = unregisteredUids.length > 0
            ? await this._findInBackendById(unregisteredUids)
            : [];

        const registeredEntities = registeredUids.map(uid => this.entityManager.getLoadedEntity(uid)) as T[];
        const entities = [...registeredEntities, ...unregisteredEntities];

        return new EntityObjectArray(entities.length, entities);
    }

    /**
     * Fetch entities by UIDs from backend.
     * Always includes soft-deleted entities since we're explicitly requesting by UID.
     */
    protected async _findInBackendById(uids: string[]): Promise<T[]> {

        const params = <QueryParameter<string[]>>{
            key: this.idField,
            comparator: 'in',
            value: uids
        };

        const dataTransferObjects = await this.entityManager.findAllByQueryParameters(
            this.entity.name,
            [params],
            [],
            -1,
            0,
            { includeDeleted: true }
        );

        return (
            await Promise.all(dataTransferObjects.map(async dto => this._transformDtoToEntity(dto)))
        ).filter(ArrayHelper.notEmpty);
    }
}